import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { PermissionsService } from '../../common/services/permissions.service';
import { Workspace } from '../workspaces.models';
import { NotifyService } from '../../common/services/notify.service';
import { ConfirmationDialogService } from '../../common/services/confirmation-dialog.service';
import { AppState } from '../../store';
import { ItemType } from '../../common/components/lists/list-item-snippet.component';
import { removeWorkspace } from '../store/workspaces.actions';

const workspaceBackground = require('../../../img/workspace.svg').default;
const folderIcon = require('../../../img/folder.svg').default;

@Component({
  selector: 'workspace-list-item',
  template: `
    <div class="generic-list-item slider-animation" [ngClass]="{ 'closed-animation': item.isRemoved }">
      <div
        class="workspace-list-item folders"
        [ngClass]="{
          selected: item.selected,
          'pending-removal': item.pending_removal,
          inactive: item.status == 'archived'
        }"
        id="workspace-{{ item.id }}"
      >
        <div class="workspace-background" (click)="goToWorkspace(item)">
          <img [src]="workspaceBackground" alt="Workspace" />
        </div>
        <div class="workspace-icon" (click)="goToWorkspace(item)">
          <div class="workspace-icon-container">
            <img [src]="folderIcon" alt="workspace" />
          </div>
        </div>
        <div class="workspace-name">
          <svg
            *ngIf="item.flow_type === 'workflow'"
            class="icon icons-master-expanded_icon-navmain-packages icon-workflow"
          >
            <use href="/img/img-sprite.svg#icon-icons-master-expanded_icon-navmain-workflow"></use>
          </svg>
          <svg *ngIf="item.flow_type === 'dataflow'" class="icon icons-master-expanded_icon-navmain-packages">
            <use href="/img/img-sprite.svg#icons-master-expanded_icon-navmain-packages"></use>
          </svg>
          <div class="name-description-container">
            <strong class="name-container">
              <a
                (click)="goToWorkspace(item)"
                class="workspace-title"
                [matTooltip]="item.name"
                matTooltipPosition="right"
                matTooltipClass="right wide"
                >{{ item.name | xpLengthCheck: 22 }}</a
              >
            </strong>
            <p
              [matTooltip]="item.description"
              matTooltipPosition="right"
              matTooltipClass="right wide"
              class="workspace-description"
            >
              {{ item.description | xpLengthCheck: 55 }}
            </p>
          </div>
        </div>
        <div class="workspace-owner">
          <a
            (click)="goToOwner(item)"
            [matTooltip]="ownerName"
            matTooltipPosition="right"
            matTooltipClass="right wide"
            >{{ ownerName | xpLengthCheck: 20 }}</a
          >
        </div>

        <div class="workspace-date">
          <span
            class="date-tooltip"
            [matTooltip]="item.updated_at | xpDateUTC"
            matTooltipPosition="above"
            matTooltipClass="above"
            >{{ item.updated_at | xpPrettyDate }}</span
          >
        </div>
        <div class="workspace-description">
          <span>{{ 'workspace.generic-list.item.labels.last-edit' | translate }}</span>
          <span
            class="date-tooltip"
            [matTooltip]="item.updated_at | xpDateUTC"
            matTooltipPosition="above"
            matTooltipClass="above"
            >{{ item.updated_at | xpPrettyDate }}</span
          >
          <span>{{ 'workspace.generic-list.item.labels.by' | translate }}</span>
          <a class="workspace-owner-description" (click)="goToOwner(item)">{{ ownerName | xpLengthCheck: 20 }}</a>
        </div>
        <div class="packages-statuses">
          <div class="packages-statuses-container">
            <span
              class="packages-status total"
              matTooltipPosition="above"
              [matTooltip]="
                'workspace.generic-list.item.labels.packages-length' | translate: { length: activePackages }
              "
              >{{ activePackages }}</span
            >
            <span
              class="packages-status failed"
              matTooltipPosition="above"
              [matTooltip]="
                'workspace.generic-list.item.labels.packages-failed' | translate: { length: failedPackages }
              "
              >{{ failedPackages }}</span
            >
            <span
              class="packages-status pending"
              matTooltipPosition="above"
              [matTooltip]="
                'workspace.generic-list.item.labels.packages-stopped' | translate: { length: pendingPackages }
              "
              >{{ pendingPackages }}</span
            >
            <span
              class="packages-status success"
              matTooltipPosition="above"
              [matTooltip]="
                'workspace.generic-list.item.labels.packages-completed' | translate: { length: successPackages }
              "
              >{{ successPackages }}</span
            >
          </div>
        </div>
        <div class="dropdown xp-dropdown" [matMenuTriggerFor]="dropdown">
          <i class="fa fa-ellipsis-v"></i>
        </div>
        <mat-menu #dropdown="matMenu">
          <li mat-menu-item (click)="editWorkspace(item)">
            <span>{{ 'workspace.generic-list.item.actions.edit' | translate }}</span>
          </li>
          <li mat-menu-item (click)="onRemove(item)">
            <span>{{ 'workspace.generic-list.item.actions.delete' | translate }}</span>
          </li>
        </mat-menu>
      </div>
    </div>
  `,
})
export class WorkspaceListItemComponent implements OnChanges {
  @Input() item: Workspace;
  @Output() edit = new EventEmitter<Workspace>();
  pendingPackages: number;
  successPackages: number;
  failedPackages: number;
  activePackages: number;
  workspaceBackground = workspaceBackground;
  folderIcon = folderIcon;

  constructor(
    private permissionsService: PermissionsService,
    private translate: TranslateService,
    private notify: NotifyService,
    private confirmationDialog: ConfirmationDialogService,
    private store: Store<AppState>,
    @Inject('$rootScope') private $rootScope: any,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.item && changes.item.currentValue) {
      this.countPackages();
    }
  }

  get ownerName(): string {
    const owner = this.item.modified_by || { name: '', email: '' };
    return owner.name || (owner.email.match(/\S+(?=@)/g) || [''])[0];
  }

  editWorkspace(item: Workspace) {
    this.edit.emit(item);
  }

  countPackages() {
    this.activePackages = (this.item.packages || []).filter((item) => item.package.status === 'active').length;
    this.pendingPackages = (this.item.packages || []).filter(
      (item) => item.package.last_job_status === 'stopped',
    ).length;
    this.successPackages = (this.item.packages || []).filter(
      (item) => item.package.last_job_status === 'success' || item.package.last_job_status === 'completed',
    ).length;
    this.failedPackages = (this.item.packages || []).filter((item) => item.package.last_job_status === 'failed').length;
  }

  hasPermission(permissionName) {
    return this.permissionsService.hasPermission(permissionName);
  }

  goToWorkspace(item: Workspace): void {
    this.$rootScope.stateGo('app.account.workspaces-item', { workspace_id: item.id });
  }

  goToOwner(item: Workspace): void {
    this.$rootScope.stateGo('app.account.settings.account.member', item.owner);
  }

  onRemove(item: Workspace): void {
    const dialogRef = this.confirmationDialog.openDialog({
      title: this.translate.instant('workspace.generic-list.item.modals.confirm.title'),
      hint: this.translate.instant('workspace.generic-list.item.modals.confirm.hint'),
      yes: this.translate.instant('workspace.generic-list.item.modals.confirm.yes'),
      no: this.translate.instant('workspace.generic-list.item.modals.confirm.no'),
      item,
      itemType: ItemType.workspace,
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.store.dispatch(removeWorkspace({ workspaceId: item.id }));
      }
    });
  }
}
